import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  MapPin, Phone, Instagram, Mail, CheckCircle2, ArrowRight, Sparkles,
  GraduationCap, Globe2, Heart, Brain, Compass, Clock, Loader2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const SLUG = 'priya-khurana';
const HERO = '/experts/priya-khurana-hero.png';
const WHATSAPP = '+971508238898';
const INSTA = '@iamtarotperspective';

const SERVICES = [
  { icon: Heart,    title: 'Relationship & Love Guidance',         desc: 'Clarity on partner dynamics, attachment patterns and communication gaps.' },
  { icon: Sparkles, title: 'Marriage & Compatibility Insights',    desc: 'Pre-marital alignment, compatibility check, family dynamics.' },
  { icon: Brain,    title: 'Emotional Awareness & Healing',        desc: 'Identify emotional triggers, release recurring patterns, regulate inner state.' },
  { icon: Compass,  title: 'Self-Discovery & Personal Growth',     desc: 'Clarify life direction, values, identity and meaningful next steps.' },
  { icon: Globe2,   title: 'Online Tarot Consultations Worldwide', desc: 'UAE • India • Canada • Singapore • UK • USA — confidential video / audio.' },
  { icon: GraduationCap, title: 'Confidential One-to-One Sessions',desc: '100% private, judgment-free, structured 45–60 min consultations.' },
];

const FAQ = [
  {
    q: 'Is Priya Khurana a fortune teller?',
    a: 'No. Priya uses tarot as a tool for self-awareness, emotional clarity and conscious decision-making — not for prediction. Her approach combines tarot wisdom with relationship coaching and attachment-style awareness.',
  },
  {
    q: 'Where is Priya Khurana based?',
    a: 'Priya is based in Dubai, UAE and offers confidential online consultations to clients in UAE, India, Canada, Singapore, UK, USA and worldwide.',
  },
  {
    q: 'What is her training and experience?',
    a: 'Priya has over 6 years of professional practice with advanced tarot training in Canada and an Advanced Tarot Certification from Singapore. She specialises in relationship coaching and self-awareness mentoring.',
  },
  {
    q: 'How do I book a session?',
    a: 'Fill the booking form below or message Priya directly on WhatsApp at +971 50 823 8898. She personally confirms every session within 24 hours.',
  },
  {
    q: 'What kind of questions does she help with?',
    a: 'Love and relationship clarity, marriage compatibility, emotional patterns, attachment styles, life direction, career transitions, personal growth and self-discovery.',
  },
];

// JSON-LD: Person + Service + FAQPage + ProfessionalService — maximum AI search visibility.
const buildJsonLd = () => ([
  {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `https://viratastro.cloud/experts/${SLUG}#person`,
    name: 'Priya Khurana',
    image: `https://viratastro.cloud${HERO}`,
    jobTitle: 'Tarot Teacher, Relationship Coach & Self-Awareness Mentor',
    description: 'Internationally trained Tarot Teacher and Relationship Coach based in Dubai. Helps individuals gain clarity in love, relationships, emotional well-being, and personal growth through insightful tarot guidance and self-awareness coaching.',
    address: { '@type': 'PostalAddress', addressLocality: 'Dubai', addressCountry: 'AE' },
    telephone: WHATSAPP,
    email: 'bookings@viratastro.cloud',
    sameAs: [
      `https://instagram.com/${INSTA.replace('@','')}`,
      `https://wa.me/${WHATSAPP.replace('+','')}`,
    ],
    knowsAbout: [
      'Tarot Reading', 'Relationship Coaching', 'Love Guidance',
      'Marriage Compatibility', 'Emotional Awareness', 'Attachment Styles',
      'Self-Discovery', 'Personal Growth', 'Mindfulness',
    ],
    alumniOf: [
      { '@type': 'EducationalOrganization', name: 'Advanced Tarot Training', address: { '@type': 'PostalAddress', addressCountry: 'CA' } },
      { '@type': 'EducationalOrganization', name: 'Advanced Tarot Certification', address: { '@type': 'PostalAddress', addressCountry: 'SG' } },
    ],
    worksFor: { '@id': 'https://viratastro.cloud#organization' },
    affiliation: { '@id': 'https://viratastro.cloud#organization' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Priya Khurana — Tarot Reading & Relationship Coaching Dubai',
    image: `https://viratastro.cloud${HERO}`,
    address: { '@type': 'PostalAddress', addressLocality: 'Dubai', addressCountry: 'AE' },
    telephone: WHATSAPP,
    priceRange: 'AED 99 — AED 729',
    areaServed: ['United Arab Emirates','India','Canada','Singapore','United Kingdom','United States'],
    serviceType: ['Tarot Reading','Relationship Coaching','Marriage Compatibility','Self-Awareness Mentoring'],
    url: `https://viratastro.cloud/experts/${SLUG}`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
]);

function ServiceCard({ icon: Icon, title, desc }) {
  return (
    <div className="group relative rounded-2xl bg-bg-card border border-border-gold/30 p-6 hover:border-gold/60 transition-all duration-300" data-testid={`priya-service-${title.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-gold/0 to-gold/0 group-hover:from-gold/5 group-hover:to-transparent rounded-2xl transition-all" />
      <div className="relative">
        <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
          <Icon className="w-6 h-6 text-gold" />
        </div>
        <h3 className="text-lg font-bold text-text-primary mb-2">{title}</h3>
        <p className="text-sm text-text-muted leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function PriyaKhuranaPage() {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', country: '',
    service: 'Tarot Reading', preferred_time: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error('Please fill name and phone');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/experts/booking`, {
        expert_slug: SLUG, ...form,
      });
      if (res.data?.booking_id) {
        setDone(true);
        toast.success('Booking received! Priya will contact you within 24 hours.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed. Please try again or WhatsApp directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      <SEO
        title="Priya Khurana — Dubai Tarot Reader, Relationship Coach & Self-Awareness Mentor"
        description="Looking for professional tarot reading in Dubai? Priya Khurana is an internationally trained Tarot Teacher and Relationship Coach helping individuals gain clarity in love, relationships, emotional well-being, and personal growth through insightful tarot guidance and self-awareness coaching."
        path={`/experts/${SLUG}`}
        image={HERO}
        type="profile"
        keywords="Tarot Reader Dubai, Online Tarot Consultation, Relationship Coach Dubai, Love Guidance, Marriage Compatibility, Self-Discovery, Priya Khurana, Tarot Teacher UAE, Attachment Style Coach, Personal Growth Mentor"
        jsonLd={buildJsonLd()}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Experts', path: '/experts' },
          { name: 'Priya Khurana', path: `/experts/${SLUG}` },
        ]}
      />

      <Header />

      {/* HERO */}
      <section className="relative pt-28 pb-16 overflow-hidden" data-testid="priya-hero">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,rgba(255,215,0,0.15),transparent_60%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.12),transparent_60%)]" />
        <div className="container mx-auto px-4 max-w-7xl relative">
          {/* Brand-hierarchy line — keeps Vivek Ji as Owner, Priya as Featured Expert */}
          <div className="text-center mb-6">
            <p className="text-[11px] tracking-[0.3em] text-gold/70 uppercase">
              <Link to="/" className="hover:text-gold transition">ViratAstro</Link>
              <span className="mx-2 text-gold/40">›</span>
              <Link to="/about" className="hover:text-gold transition">Founded by Acharya Vivek Mudgal</Link>
              <span className="mx-2 text-gold/40">›</span>
              <span className="text-gold">Featured Expert</span>
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 text-gold text-xs tracking-widest uppercase mb-5">
                <Sparkles className="w-3 h-3" /> Featured Tarot Expert · ViratAstro
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-4">
                Priya Khurana
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gold/90 font-light leading-snug mb-5">
                Dubai-Based Tarot Teacher, Relationship Coach &amp; Self-Awareness Mentor
              </p>
              <p className="text-sm sm:text-base text-text-muted leading-relaxed mb-6">
                With over <b className="text-text-primary">6 years of professional experience</b> and advanced tarot training in
                <b className="text-text-primary"> Canada</b> &amp; <b className="text-text-primary"> Singapore</b>, Priya helps individuals gain clarity in love, relationships, emotional well-being and personal growth.
              </p>
              <p className="text-sm text-text-muted leading-relaxed mb-7">
                Her approach combines <b className="text-text-primary">tarot wisdom with relationship coaching</b>, attachment style awareness and self-discovery practices. Rather than focusing on predictions, she empowers clients to make informed decisions through deeper self-awareness and meaningful insights.
              </p>

              <div className="flex flex-wrap gap-3">
                <a
                  href={`https://wa.me/${WHATSAPP.replace('+','')}?text=${encodeURIComponent('Namaste Priya, I would like to book a Tarot session.')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition shadow-lg shadow-green-900/30"
                  data-testid="priya-whatsapp-cta"
                >
                  <Phone className="w-4 h-4" /> WhatsApp +971 50 823 8898
                </a>
                <a
                  href="#book"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-bg-primary font-semibold text-sm hover:bg-yellow-400 transition"
                  data-testid="priya-book-cta"
                >
                  Book a Session <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <div className="flex flex-wrap gap-5 mt-7 text-xs text-text-muted">
                <span className="flex items-center gap-1"><Instagram className="w-4 h-4 text-pink-400" /> {INSTA}</span>
                <span className="flex items-center gap-1"><Mail className="w-4 h-4 text-gold" /> bookings@viratastro.cloud</span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 via-purple-500/10 to-pink-500/10 blur-3xl rounded-[3rem]" />
              <img
                src={HERO}
                alt="Priya Khurana — Dubai-based Tarot Teacher, Relationship Coach and Self-Awareness Mentor — Featured Expert at ViratAstro"
                className="relative w-full rounded-[2rem] border border-gold/30 shadow-2xl"
                loading="eager"
                data-testid="priya-hero-image"
              />
            </div>
          </div>

          {/* Endorsement line — reinforces Vivek Ji as the brand authority */}
          <div className="mt-10 text-center">
            <p className="text-xs text-text-muted italic max-w-2xl mx-auto">
              <Sparkles className="w-3 h-3 inline text-gold mr-1" />
              Brought to you by <Link to="/about" className="text-gold hover:underline font-semibold not-italic">Acharya Vivek Mudgal</Link>
              <span className="text-gold/40 mx-2">·</span>
              Founder of ViratAstro · 33+ years of Vedic experience curating world-class spiritual experts for you.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES — 6 EEAT signals */}
      <section className="py-14 bg-bg-secondary" data-testid="priya-services">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Services Offered</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-2">How Priya can help you</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => <ServiceCard key={s.title} {...s} />)}
          </div>
        </div>
      </section>

      {/* APPROACH BLOCK */}
      <section className="py-14" data-testid="priya-approach">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="rounded-3xl bg-gradient-to-br from-bg-card to-bg-secondary border border-gold/20 p-8 sm:p-10">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Approach</span>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold mt-2 mb-5">Tarot as a tool for clarity — not prediction</h2>
            <p className="text-text-muted leading-relaxed mb-4">
              Priya does <b className="text-text-primary">not</b> tell fortunes. She uses tarot as a structured mirror — a way to surface unconscious patterns, name unspoken feelings, and create a calm space for reflection.
            </p>
            <p className="text-text-muted leading-relaxed mb-4">
              Drawing from advanced training in <b className="text-text-primary">attachment theory, emotional regulation, and modern coaching frameworks</b>, every session is built around your specific question — whether about a relationship, a recurring emotional pattern, a major life decision, or your personal growth.
            </p>
            <p className="text-text-muted leading-relaxed">
              Clients across <b className="text-text-primary">UAE, India, Canada, Singapore, UK and the US</b> trust Priya for her warm, intelligent, and judgment-free presence.
            </p>
          </div>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="book" className="py-16 bg-bg-secondary" data-testid="priya-booking">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Book a Session</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-2">Schedule your consultation with Priya</h2>
            <p className="text-text-muted mt-3 text-sm">Confidential one-to-one, 45-60 min. Priya personally confirms every booking within 24 hours.</p>
          </div>

          {done ? (
            <div className="rounded-2xl bg-green-500/10 border border-green-500/40 p-8 text-center" data-testid="priya-booking-success">
              <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-300 mb-2">Booking received!</h3>
              <p className="text-text-muted text-sm">Priya will reach out on WhatsApp within 24 hours to confirm your session time. Check your email for the booking reference.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl bg-bg-card border border-gold/20 p-6 sm:p-8 space-y-4" data-testid="priya-booking-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-name" required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 rounded-lg bg-bg-primary border border-border-gold/30 text-text-primary focus:border-gold outline-none" />
                <input data-testid="booking-phone" required placeholder="WhatsApp number (with country code)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-4 py-3 rounded-lg bg-bg-primary border border-border-gold/30 text-text-primary focus:border-gold outline-none" />
                <input data-testid="booking-email" placeholder="Email (optional)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-4 py-3 rounded-lg bg-bg-primary border border-border-gold/30 text-text-primary focus:border-gold outline-none" />
                <input data-testid="booking-country" placeholder="Country (e.g. UAE, India)" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="px-4 py-3 rounded-lg bg-bg-primary border border-border-gold/30 text-text-primary focus:border-gold outline-none" />
                <select data-testid="booking-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="px-4 py-3 rounded-lg bg-bg-primary border border-border-gold/30 text-text-primary focus:border-gold outline-none">
                  <option>Tarot Reading</option>
                  <option>Relationship Coaching</option>
                  <option>Marriage Compatibility</option>
                  <option>Self-Discovery</option>
                  <option>Other</option>
                </select>
                <input data-testid="booking-time" placeholder="Preferred time (e.g. Sat 6pm GST)" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="px-4 py-3 rounded-lg bg-bg-primary border border-border-gold/30 text-text-primary focus:border-gold outline-none" />
              </div>
              <textarea data-testid="booking-message" rows={3} placeholder="Briefly describe what you'd like clarity on (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-bg-primary border border-border-gold/30 text-text-primary focus:border-gold outline-none" />
              <button type="submit" disabled={submitting} className="w-full py-3 rounded-full bg-gold text-bg-primary font-semibold hover:bg-yellow-400 transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="booking-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Submit Booking <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-xs text-text-muted text-center"><Clock className="w-3 h-3 inline mr-1" /> Confirmation within 24 hours via WhatsApp</p>
            </form>
          )}
        </div>
      </section>

      {/* FAQ — for FAQPage JSON-LD + AI Search */}
      <section className="py-14" data-testid="priya-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">FAQ</span>
            <h2 className="text-3xl font-heading font-bold mt-2">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map(({ q, a }, i) => (
              <details key={i} className="group rounded-xl bg-bg-card border border-border-gold/20 p-5 hover:border-gold/40 transition" data-testid={`priya-faq-${i}`}>
                <summary className="font-semibold text-text-primary cursor-pointer list-none flex justify-between items-center">
                  {q}
                  <span className="text-gold text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-text-muted leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKING */}
      <section className="py-10 bg-bg-secondary">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <p className="text-xs tracking-[0.3em] text-gold/70 uppercase mb-3">Explore</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/daily-tarot" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Daily Tarot Card →</Link>
            <Link to="/poocho-guruji" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Pucho Guruji (Vedic)</Link>
            <Link to="/about" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">About Vivek Mudgal</Link>
            <Link to="/" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Home</Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}
